import Utils from "../../../../../../../../core/services/utils/utils";
import {PreventionInvalidityCode} from "../preventative-interactions";
import {BaseImportTransformer, toPartNumberUpper} from "./shared";

const normalizePartnerNameKey = (name) => name?.trim()?.toUpperCase() ?? "";

const warehouseFieldKey = (warehouseId) => `warehouse_${warehouseId}`;

const normalizePartnerNameKeyFromCell = (partnerNameCell) => {
    if (partnerNameCell == null) {
        return "";
    }
    if (typeof partnerNameCell === "string") {
        return normalizePartnerNameKey(partnerNameCell);
    }
    return normalizePartnerNameKey(partnerNameCell?.sellerInfo?.name);
};

const parsePartnerConnectionId = (raw) => {
    if (raw === null || raw === undefined || String(raw).trim() === "") {
        return null;
    }
    const parsed = parseInt(String(raw).trim(), 10);
    return Number.isNaN(parsed) ? null : parsed;
};

const parseSellerPartId = (raw) => {
    if (raw === null || raw === undefined || String(raw).trim() === "") {
        return null;
    }
    const parsed = parseInt(String(raw).trim(), 10);
    return Number.isNaN(parsed) ? null : parsed;
};

const hasPartnerIdentifier = (item) => {
    const hasId = parsePartnerConnectionId(item?.partnerConnectionId) != null;
    const hasName = !!normalizePartnerNameKeyFromCell(item?.partnerName);
    return hasId || hasName;
};

/**
 * Prefer warehouse code `00` first (main warehouse), then stable id order.
 * @param {Array<*>} warehouses
 * @returns {Array<*>}
 */
export const sortWarehousesForPartnerImport = (warehouses) => {
    const mainWarehouse = warehouses?.find((e) => e.isMain) ?? warehouses?.find((e) => e.code === '00');
    const warehousesWithMainWarehouseFirst = [...(warehouses ?? [])];
    warehousesWithMainWarehouseFirst.sort((a, b) => {
        if (mainWarehouse?.id === a.id) return -1;
        if (mainWarehouse?.id === b.id) return 1;
        return String(a?.id ?? '').localeCompare(String(b?.id ?? ''));
    });
    return warehousesWithMainWarehouseFirst;
};

/**
 * @param {*} item
 * @param {Array<*>} warehouses
 * @returns {{ warehouseIds: Array<number|string>, warehouseLabels: string[] }}
 */
export const collectSelectedWarehousesFromRow = (item, warehouses) => {
    const ordered = sortWarehousesForPartnerImport(warehouses);
    const warehouseIds = [];
    const warehouseLabels = [];
    for (const warehouse of ordered) {
        if (warehouse?.id == null) {
            continue;
        }
        const raw = item?.[warehouseFieldKey(warehouse.id)];
        if (Utils.tryConvertStringToBoolean(raw) !== true) {
            continue;
        }
        warehouseIds.push(warehouse.id);
        warehouseLabels.push(warehouse.code ?? warehouse.title ?? String(warehouse.id));
    }
    return {warehouseIds, warehouseLabels};
};

/**
 * @param {Array<*>} approvedPartners
 * @returns {{ byId: Record<string, *>, byName: Record<string, *> }}
 */
export const buildPartnerConnectionMaps = (approvedPartners) => {
    const byId = {};
    const byName = {};
    for (const partner of approvedPartners ?? []) {
        if (partner?.id != null && partner.id !== "") {
            byId[String(partner.id)] = partner;
        }
        const nameKey = normalizePartnerNameKey(partner?.sellerInfo?.name);
        if (nameKey && !byName[nameKey]) {
            byName[nameKey] = partner;
        }
    }
    return {byId, byName};
};

/**
 * @param {*} item
 * @param {{ byId: Record<string, *>, byName: Record<string, *> }} maps
 * @returns {*|null}
 */
export const resolvePartnerFromImportRow = (item, maps) => {
    const connectionId = parsePartnerConnectionId(item?.partnerConnectionId);
    const nameKey = normalizePartnerNameKeyFromCell(item?.partnerName);

    if (connectionId != null && maps.byId[String(connectionId)]) {
        return maps.byId[String(connectionId)];
    }
    if (nameKey && maps.byName[nameKey]) {
        return maps.byName[nameKey];
    }
    return null;
};

/**
 * Products from another partner — validates partner connection; seller part id is taken from CSV as-is.
 * Optional warehouse_* True/False columns select warehouses to create after a successful copy.
 */
export class ProductsFromAnotherPartnerImportTransformer extends BaseImportTransformer {
    /**
     * @param {Array<Object|null|undefined>} mappedData
     * @param {boolean} isFinalRun
     * @param {Array<*>} [approvedPartners]
     * @param {Array<*>} [allWarehouses]
     */
    async run(mappedData, isFinalRun = false, approvedPartners = [], allWarehouses = []) {
        const rows = mappedData ?? [];
        const preventativeIssues = [];
        const partnerMaps = buildPartnerConnectionMaps(approvedPartners);

        /** @type {{index: number, item: *, partId: *, partNumber: string, keepUpdate: *, partner: *, codes: string[]}[]} */
        const rowState = rows.map((item, index) => ({
            index,
            item,
            partId: parseSellerPartId(item?.partId),
            partNumber: toPartNumberUpper(item?.partNumber),
            keepUpdate: item?.keepUpdate,
            partner: null,
            codes: [],
        }));

        for (const rs of rowState) {
            if (!rs.item) {
                continue;
            }
            if (rs.partId == null || !rs.partNumber) {
                rs.codes.push(PreventionInvalidityCode.MISSING_REQUIRED_FIELDS);
            }
            if (!hasPartnerIdentifier(rs.item)) {
                rs.codes.push(PreventionInvalidityCode.INVALID_PARTNER);
            }
            if (
                rs.item.keepUpdate !== undefined &&
                rs.item.keepUpdate !== null &&
                String(rs.item.keepUpdate).trim() !== "" &&
                Utils.tryConvertStringToBoolean(rs.item.keepUpdate) === undefined
            ) {
                rs.codes.push(PreventionInvalidityCode.INVALID_BOOLEAN);
            }
        }

        for (const rs of rowState) {
            if (!rs.item || !hasPartnerIdentifier(rs.item)) {
                continue;
            }
            rs.partner = resolvePartnerFromImportRow(rs.item, partnerMaps);
            if (!rs.partner) {
                rs.codes.push(PreventionInvalidityCode.INVALID_PARTNER);
            }
        }

        for (const rs of rowState) {
            if (!rs.item) {
                preventativeIssues.push({
                    rowIndex: rs.index,
                    originalRow: rows[rs.index],
                    invalidityCodes: [PreventionInvalidityCode.MISSING_REQUIRED_FIELDS],
                });
                continue;
            }
            if (!rs.codes.length) {
                continue;
            }
            preventativeIssues.push({
                rowIndex: rs.index,
                originalRow: rs.item,
                invalidityCodes: [...new Set(rs.codes)],
            });
        }

        const issueIndexSet = new Set(preventativeIssues.map((p) => p.rowIndex));

        const apiRequests = rows.map((item, index) => {
            const {warehouseIds, warehouseLabels} = collectSelectedWarehousesFromRow(item, allWarehouses);

            if (!item) {
                return isFinalRun
                    ? null
                    : {
                        partId: null,
                        partnerConnectionId: null,
                        partnerName: null,
                        partNumber: null,
                        keepUpdate: true,
                        warehouseIds: [],
                        warehouseLabels: [],
                    };
            }

            const rs = rowState[index];
            const partner = rs.partner;
            const keepUpdateBool = Utils.tryConvertStringToBoolean(rs.item.keepUpdate);
            const keepUpdate = keepUpdateBool === undefined ? true : keepUpdateBool;
            const partnerName =
                partner?.sellerInfo?.name ??
                (typeof item.partnerName === "string" ? item.partnerName?.trim() : item.partnerName?.sellerInfo?.name) ??
                null;

            if (issueIndexSet.has(index)) {
                return isFinalRun
                    ? null
                    : {
                        partId: rs.partId,
                        partnerConnectionId: partner?.id ?? parsePartnerConnectionId(item.partnerConnectionId),
                        partnerName,
                        partNumber: rs.partNumber || item.partNumber,
                        keepUpdate,
                        warehouseIds,
                        warehouseLabels,
                    };
            }

            return {
                partId: rs.partId,
                partnerConnectionId: partner.id,
                partnerName,
                partNumber: rs.partNumber,
                keepUpdate,
                warehouseIds,
                warehouseLabels,
            };
        });

        return {apiRequests, preventativeIssues};
    }
}
